import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";
import type { RecentHovRateSegmentToken } from "@/app/game/recentHovRateTracker";
import { compareHuge } from "@/shared/numbers";

export type AutoResetReadoutLayer = "mhov" | "nhov" | "ohov";

export interface HovSurfaceReadoutSnapshotMemory {
  readonly seedReadableSnapshot: (snapshot: HoverIdleSnapshot) => void;
  readonly latchAutoResetSnapshot: (options: {
    readonly layer: AutoResetReadoutLayer;
    readonly snapshot: HoverIdleSnapshot;
    readonly recentHovRateSegmentToken?: RecentHovRateSegmentToken | null;
  }) => void;
  readonly resolveReadoutPresentation: (
    options: ResolveHovSurfaceReadoutOptions,
  ) => HovSurfaceReadoutPresentation;
  readonly resolveReadoutSnapshot: (options: {
    readonly authoritativeSnapshot: HoverIdleSnapshot;
    readonly holdAutoResetReadout: boolean;
  }) => HoverIdleSnapshot;
}

export interface ResolveHovSurfaceReadoutOptions {
  readonly authoritativeSnapshot: HoverIdleSnapshot;
  readonly holdAutoResetReadout: boolean;
}

export interface HovSurfaceReadoutPresentation {
  readonly snapshot: HoverIdleSnapshot;
  readonly recentHovRateSegmentToken: RecentHovRateSegmentToken | null;
}

export function createHovSurfaceReadoutSnapshotMemory(): HovSurfaceReadoutSnapshotMemory {
  let lastReadableSnapshot: HoverIdleSnapshot | null = null;
  let autoResetLatch: {
    readonly layer: AutoResetReadoutLayer;
    readonly snapshot: HoverIdleSnapshot;
    readonly recentHovRateSegmentToken: RecentHovRateSegmentToken | null;
  } | null = null;

  const rememberIfReadable = (snapshot: HoverIdleSnapshot): boolean => {
    if (!isReadableHovSurfaceSnapshot(snapshot)) {
      return false;
    }
    lastReadableSnapshot = snapshot;
    return true;
  };

  const resolveReadoutPresentation = (
    options: ResolveHovSurfaceReadoutOptions,
  ): HovSurfaceReadoutPresentation => {
    if (options.holdAutoResetReadout && autoResetLatch !== null) {
      return {
        snapshot: autoResetLatch.snapshot,
        recentHovRateSegmentToken: autoResetLatch.recentHovRateSegmentToken,
      };
    }

    if (autoResetLatch !== null) {
      // Reached only on a settled frame, since the held path above returns
      // while holdAutoResetReadout is true. The auto reset burst is over, so
      // drop the pre-reset latch and surface the authoritative state even when
      // it is not readable: a post-reset tier-1 / zero-ish snapshot is the
      // settled state to show, mirroring the target structure settle release.
      // Clear lastReadableSnapshot when the authoritative state is not
      // readable so no other retention path keeps the stale pre-reset value.
      autoResetLatch = null;
      if (!rememberIfReadable(options.authoritativeSnapshot)) {
        lastReadableSnapshot = null;
      }
      return {
        snapshot: options.authoritativeSnapshot,
        recentHovRateSegmentToken: null,
      };
    }

    if (rememberIfReadable(options.authoritativeSnapshot)) {
      return {
        snapshot: options.authoritativeSnapshot,
        recentHovRateSegmentToken: null,
      };
    }

    if (options.holdAutoResetReadout && lastReadableSnapshot !== null) {
      return {
        snapshot: lastReadableSnapshot,
        recentHovRateSegmentToken: null,
      };
    }

    if (!options.holdAutoResetReadout) {
      lastReadableSnapshot = null;
    }
    return {
      snapshot: options.authoritativeSnapshot,
      recentHovRateSegmentToken: null,
    };
  };

  return {
    seedReadableSnapshot(snapshot): void {
      rememberIfReadable(snapshot);
    },

    latchAutoResetSnapshot(options): void {
      if (
        autoResetLatch !== null &&
        AUTO_RESET_READOUT_PRIORITY[options.layer] <
          AUTO_RESET_READOUT_PRIORITY[autoResetLatch.layer]
      ) {
        return;
      }
      autoResetLatch = {
        ...options,
        recentHovRateSegmentToken: options.recentHovRateSegmentToken ?? null,
      };
    },

    resolveReadoutPresentation(
      options: ResolveHovSurfaceReadoutOptions,
    ): HovSurfaceReadoutPresentation {
      return resolveReadoutPresentation(options);
    },

    resolveReadoutSnapshot(options): HoverIdleSnapshot {
      return resolveReadoutPresentation(options).snapshot;
    },
  };
}

const AUTO_RESET_READOUT_PRIORITY: Readonly<
  Record<AutoResetReadoutLayer, number>
> = {
  mhov: 1,
  nhov: 2,
  ohov: 3,
};

function isReadableHovSurfaceSnapshot(snapshot: HoverIdleSnapshot): boolean {
  return (
    compareHuge(snapshot.hov, 0) > 0 ||
    compareHuge(snapshot.mHov.currentLoopHovEarned, 0) > 0 ||
    snapshot.mHov.shouldShow ||
    snapshot.nHov.canExecute ||
    snapshot.oHov.canExecute ||
    snapshot.oHov.progressRatio > 0
  );
}
